// Lightweight payload normalizer/pruner for cards.base_payload
// Keeps a small, safe set of fields per card and removes obvious noisy keys.
export function normalizeCardPayload(payload: any, cardType?: string) {
  const warnings: string[] = [];
  if (!payload || typeof payload !== 'object') return { pruned: {}, warnings, size: 0 };

  // base whitelist (restaurant-focused but safe as a default)
  const baseWhitelist = [
    'title','description','address','cover_image','website','phone','price_range','duration_minutes',
    'opening_hours','tags','metaPairs','cuisine','signatureDishes','restaurantName','bookingTips',
    'platform_product_id','platform_product_ids','location','latitude','longitude','images',
    // video card fields
    'embed','type','card_type','source','favicon','siteName','imageUrl'
  ];

  // allow some card-type-specific extras in future
  const whitelist = new Set(baseWhitelist.map(k => k.toString()));

  const pruned: any = {};

  const isNoisyKey = (k: string) => /itinerar|editor|raw|debug|payload|state|__proto__|window|document/i.test(k);

  try {
    for (const key of Object.keys(payload)) {
      if (isNoisyKey(key)) {
        warnings.push(`removed noisy key: ${key}`);
        continue;
      }
      if (whitelist.has(key)) {
        const v = payload[key];
        // simple sanitization: drop functions, convert numeric-like strings to numbers for lat/lon/duration
        if (typeof v === 'function') { warnings.push(`dropped function for key ${key}`); continue; }
        if ((key === 'latitude' || key === 'longitude' || key === 'duration_minutes') && typeof v === 'string') {
          const n = Number(v);
          if (Number.isFinite(n)) pruned[key] = n; else { pruned[key] = v; warnings.push(`could not coerce ${key} to number`); }
          continue;
        }
        // arrays & objects: shallow clone
        if (Array.isArray(v)) pruned[key] = v.slice(0, 1000); else if (v && typeof v === 'object') pruned[key] = JSON.parse(JSON.stringify(v)); else pruned[key] = v;
      } else {
        // not on whitelist: if it's small primitive, keep under a safe name? For now drop and warn.
        const v = payload[key];
        if (v === null || typeof v === 'undefined') continue;
        if (typeof v === 'string' && v.length <= 128) {
          // allow small helper strings like 'subtitle'
          pruned[key] = v;
          continue;
        }
        warnings.push(`dropped non-whitelisted key: ${key}`);
      }
    }
  } catch (e) {
    warnings.push('normalize exception');
  }

  // size check
  let size = 0;
  try { size = JSON.stringify(pruned).length; } catch (e) { size = 0; }
  const MAX = 20000; // 20 KB threshold
  if (size > MAX) {
    warnings.push(`pruned payload too large (${size} bytes)`);
  }

  return { pruned, warnings, size };
}

export default normalizeCardPayload;
